import { useState } from 'react';
import {
  makeStyles, tokens, Title2, Title3, Body1, Caption1, Spinner,
  MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { ProjectPicker } from '../../../components/ProjectPicker';
import { useCycleTime } from '../../../api/hooks';

// FEAT-029 WIP Cycle Time (measure-only). Total cycle time histogram per project; the
// per-stage breakdown is deferred per the API caveat.
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  toolbar: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  stat: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  statValue: { fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold },
  statLabel: { color: tokens.colorNeutralForeground2 },
  chartCard: {
    height: '20rem',
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
});

function hours(n: number): string {
  if (n < 8) return `${n.toFixed(1)}h`;
  if (n < 80) return `${(n / 8).toFixed(1)}d`;
  return `${(n / 40).toFixed(1)}w`;
}

export function CycleTimePage() {
  const styles = useStyles();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { data, isLoading, error } = useCycleTime(projectId, 90);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Title2>WIP Cycle Time</Title2>
        <Body1 className={styles.hint}>
          Measure-only dashboard. Total time from Created → Closed, in working hours. Use to decide whether WIP limits / SLAs should be set.
        </Body1>
        <ProjectPicker selectedProjectId={projectId} onSelect={setProjectId} />
      </div>

      {!projectId ? (
        <Body1>Pick a project.</Body1>
      ) : isLoading ? (
        <Spinner label="Crunching cycle times…" />
      ) : error ? (
        <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>
      ) : !data ? (
        <Body1>No data.</Body1>
      ) : (
        <>
          <MessageBar intent="info">
            <MessageBarBody>{data.caveat}</MessageBarBody>
          </MessageBar>

          <Title3>Distribution</Title3>
          <div className={styles.stats}>
            <div className={styles.stat}>
              <Caption1 className={styles.statLabel}>P50</Caption1>
              <span className={styles.statValue}>{hours(data.total.p50Hours)}</span>
            </div>
            <div className={styles.stat}>
              <Caption1 className={styles.statLabel}>P85</Caption1>
              <span className={styles.statValue}>{hours(data.total.p85Hours)}</span>
            </div>
            <div className={styles.stat}>
              <Caption1 className={styles.statLabel}>P95</Caption1>
              <span className={styles.statValue}>{hours(data.total.p95Hours)}</span>
            </div>
            <div className={styles.stat}>
              <Caption1 className={styles.statLabel}>Sample size</Caption1>
              <span className={styles.statValue}>{data.total.sampleSize}</span>
            </div>
          </div>

          {data.histogram.length > 0 && (
            <div className={styles.chartCard}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.histogram}>
                  <XAxis dataKey="bucketHours" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill={tokens.colorBrandBackground} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {data.perStage.length > 0 && (
            <>
              <Title3>Time spent in each state</Title3>
              {data.perStage.map((s) => (
                <div key={s.state} className={styles.chartCard} style={{ height: '14rem' }}>
                  <Caption1 className={styles.statLabel}>
                    <strong>{s.state}</strong> · P50 {hours(s.stats.p50Hours)} · P85 {hours(s.stats.p85Hours)} · P95 {hours(s.stats.p95Hours)} · n={s.stats.sampleSize}
                  </Caption1>
                  <ResponsiveContainer width="100%" height="80%">
                    <BarChart data={s.histogram}>
                      <XAxis dataKey="bucketHours" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill={tokens.colorBrandBackground2} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}
